import React, { useState, useRef } from "react";
import { FastAverageColor } from "fast-average-color";

interface ImageWithSkeletonProps {
  src: string;
  alt: string;
  className?: string;
  skeletonClassName?: string;
}

export const ImageWithSkeleton: React.FC<ImageWithSkeletonProps> = ({
  src,
  alt,
  className = "",
  skeletonClassName = "",
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasBorder, setHasBorder] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  const isWhiteColor = (rgb: [number, number, number]): boolean => {
    const [r, g, b] = rgb;

    return r === 245 && g === 250 && b === 255;
  };

  const handleLoad = async () => {
    setIsLoading(false);

    // Extract dominant color after image loads
    if (imgRef.current) {
      try {
        const fac = new FastAverageColor();
        const color = await fac.getColorAsync(imgRef.current);

        // color.value is already [r, g, b] array
        const dominantColor: [number, number, number] = [
          color.value[0],
          color.value[1],
          color.value[2],
        ];

        if (isWhiteColor(dominantColor)) {
          setHasBorder(true);
        }
      } catch (error) {
        console.warn("Could not extract color from image:", error);
      }
    }
  };

  return (
    <div className="relative inline-block">
      {isLoading && (
        <div
          className={`absolute inset-0 bg-gray-200 animate-pulse rounded ${skeletonClassName}`}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-shimmer"></div>
        </div>
      )}

      <img
        ref={imgRef}
        src={src}
        alt={alt}
        crossOrigin="anonymous"
        className={`relative ${className} ${
          isLoading
            ? "opacity-0"
            : "opacity-100 transition-opacity duration-300"
        } ${hasBorder ? "bg-gray-400 rounded-full" : ""}`}
        onLoad={handleLoad}
        loading="lazy"
        decoding="async"
      />
    </div>
  );
};
